'use client'

import './Slider.css'

import { useEffect, useState } from 'react'
import { A11y, Keyboard, Mousewheel, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { StylePanelProps } from '@/lib/stylePanel'
import useResponsivePadding from '@/lib/useResponsivePadding'

import { PhnxSectiontitle } from '../../01-atoms/Sectiontitle/Sectiontitle'
import styles from './Gridpanel.module.css'

export type GridpanelProps = {
  preTitle?: string
  title?: string
  titleUpperCase?: boolean
  titleBold?: boolean
  postTitle?: React.ReactNode
  items?: React.ReactNode[] | any[] | any
  button?: React.ReactNode
  itemsPerRow: 'one' | 'two' | 'three' | 'four' | 'three-four'
  force2col?: boolean
  feature?: boolean
  cssStyle?: string
  flexFull?: boolean
  compact?: boolean
  borderStyle?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary'
    | 'quinary'
    | 'senary'
    | 'septenary'
  featureVariant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary'
    | 'quinary'
    | 'senary'
    | 'septenary'
  sliderOnMobile?: boolean
  darkPagination?: boolean
  fullWidth?: boolean
} & StylePanelProps

export const PhnxGridpanel: React.FC<GridpanelProps> = (props) => {
  const {
    preTitle,
    title,
    titleUpperCase = false,
    titleBold = false,
    postTitle,
    itemsPerRow = 'four',
    force2col = false,
    items = [],
    button,
    padding,
    backgroundColor,
    textColor,
    alignment = 'left',
    feature,
    cssStyle,
    flexFull,
    compact = false,
    borderStyle = 'primary',
    featureVariant = 'quaternary',
    sliderOnMobile = false,
    darkPagination,
    fullWidth,
  } = props

  const [hasMounted, setHasMounted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const paddingStyles = useResponsivePadding(padding)

  useEffect(() => {
    setHasMounted(true)
    const mediaQuery = window.matchMedia('(min-width: 600px)')
    const updateMedia = () => setIsDesktop(mediaQuery.matches)
    updateMedia()
    mediaQuery.addEventListener('change', updateMedia)
    return () => mediaQuery.removeEventListener('change', updateMedia)
  }, [])

  if (!hasMounted) return null

  const itemsCount = Array.isArray(items) ? items.length : 0
  const style = { ...paddingStyles, backgroundColor, color: textColor }

  let itemsClass = `${styles.items}
    ${styles[`items-per-row-${itemsPerRow}`]}
    ${styles[feature ? 'grid-feature' : '']}
    ${styles[compact ? 'grid-compact' : '']}
    ${flexFull ? styles['flex-full'] : ''}
    ${
      styles[
        itemsPerRow === 'four'
          ? itemsCount === 2
            ? 'halfwidth'
            : itemsCount === 3
              ? 'twothirds'
              : ''
          : ''
      ]
    }`

  if (!isDesktop && sliderOnMobile) {
    return (
      <div
        style={style}
        className={`stylepanel ${styles[`variant-${featureVariant}`]} ${styles['mobile']}`}
      >
        <div
          className={`${styles['gridpanel']} ${styles[`alignment-${alignment}`]} ${fullWidth && itemsPerRow === 'four' ? '' : 'container'} ${styles[`items-${itemsPerRow}`]} ${styles[`cssStyle-${cssStyle}`]} ${flexFull ? styles['flex-full'] : ''} ${styles['mobile']} ${fullWidth && itemsPerRow === 'four' ? styles['items-four-fullwidth'] : ''}`}
        >
          {(preTitle || title || postTitle) && (
            <div
              className={`${styles['title-container']} ${styles[feature ? 'grid-feature' : '']}`}
            >
              {featureVariant === 'quinary' ? (
                <PhnxSectiontitle title={title ?? ''} variant={'quinary'} variantBold={titleBold} />
              ) : featureVariant === 'primary' ? (
                <PhnxSectiontitle
                  title={preTitle ?? ''}
                  subtitle={title ?? ''}
                  variant={'quaternary'}
                  variantBold={titleBold}
                />
              ) : (
                <PhnxSectiontitle
                  title={preTitle ?? ''}
                  subtitle={title ?? ''}
                  variant={
                    featureVariant === 'senary' || featureVariant === 'septenary'
                      ? 'primary'
                      : featureVariant
                        ? featureVariant
                        : 'quaternary'
                  }
                  variantBold={titleBold}
                />
              )}
              {postTitle && <div className={`post-title ${styles['post-title']}`}>{postTitle}</div>}
            </div>
          )}

          {items && (
            <Swiper
              slidesPerView={1.5}
              spaceBetween={30}
              keyboard={{ enabled: true }}
              mousewheel={true}
              pagination={{ clickable: true, el: '.swiper-custom-pagination' }}
              modules={[A11y, Keyboard, Mousewheel, Pagination]}
              className="gridSwiper"
            >
              {feature && itemsPerRow !== 'one' ? (
                <div className={styles.itemsFlex}>{items}</div>
              ) : (
                items.map((item: any, index: number) => (
                  <SwiperSlide key={index}>
                    <div className={`${styles[`style-${borderStyle}`]}`}>{item}</div>
                  </SwiperSlide>
                ))
              )}
              <div className="swiper-controls">
                <div className={`swiper-custom-pagination ${darkPagination ? 'dark' : ''}`}></div>
              </div>
            </Swiper>
          )}

          {button && <div className={styles.button}>{button}</div>}
        </div>
      </div>
    )
  }

  return (
    <div
      style={style}
      className={`stylepanel ${force2col ? styles['force2col'] : ''} ${styles[`variant-${featureVariant}`]}`}
    >
      <div
        className={`${styles['gridpanel']} ${styles[`alignment-${alignment}`]} ${fullWidth && itemsPerRow === 'four' ? '' : 'container'} ${styles[`items-${itemsPerRow}`]} ${styles[`cssStyle-${cssStyle}`]} ${flexFull ? styles['flex-full'] : ''} ${fullWidth && itemsPerRow === 'four' ? styles['items-four-fullwidth'] : ''}`}
      >
        {(preTitle || title || postTitle) && (
          <div className={`${styles['title-container']} ${styles[feature ? 'grid-feature' : '']}`}>
            {feature ? (
              <>
                {preTitle && <div className={`${styles['pre-title']}`}>{preTitle}</div>}
                {title && (
                  <div className={`${styles.heading} ${titleUpperCase ? 'uppercase' : ''}`}>
                    <h2>{title}</h2>
                  </div>
                )}
              </>
            ) : featureVariant === 'quinary' ? (
              <PhnxSectiontitle title={title ?? ''} variant={'quinary'} variantBold={titleBold} />
            ) : featureVariant === 'primary' ? (
              <PhnxSectiontitle
                title={preTitle ?? ''}
                subtitle={title ?? ''}
                variant={'quaternary'}
                variantBold={titleBold}
              />
            ) : (
              <PhnxSectiontitle
                title={preTitle ?? ''}
                subtitle={title ?? ''}
                variant={
                  featureVariant === 'senary' || featureVariant === 'septenary'
                    ? 'primary'
                    : featureVariant
                      ? featureVariant
                      : 'quaternary'
                }
                variantBold={titleBold}
              />
            )}
            {postTitle && <div className={`post-title ${styles['post-title']}`}>{postTitle}</div>}
          </div>
        )}

        <div className={styles['gridContainer']}>
          {items && (
            <div className={itemsClass}>
              {itemsPerRow === 'three-four' ? (
                <>
                  <div className={styles.topRow}>
                    {items.slice(0, 3).map((item: any, index: number) => (
                      <div className={`${styles[`style-${borderStyle}`]}`} key={`top-${index}`}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className={styles.bottomRow}>
                    {items.slice(3).map((item: any, index: number) => (
                      <div className={`${styles[`style-${borderStyle}`]}`} key={`bottom-${index}`}>
                        {item}
                      </div>
                    ))}
                  </div>
                </>
              ) : feature && itemsPerRow !== 'one' ? (
                <>{items}</>
              ) : (
                items.map((item: any, index: number) => (
                  <div className={`${styles[`style-${borderStyle}`]}`} key={index}>
                    {item}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        {button && <div className={styles.button}>{button}</div>}
      </div>
    </div>
  )
}
